import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import ModalWrapper from '~/components/ModalWrapper';
import ModalHeader from '~/components/ModalWrapper/ModalHeader';
import Text from '~/components/Text';
import Button from '~/components/Button';
import Gap from '~/components/Gap';
import StyledInput from '~/components/StyledInput';
import StyledTextArea from '~/components/StyledTextArea';
import EmailPreview from '~/components/EmailPreview';
import { checkInvoiceSendApi, sendInvoiceApi } from '~/api';
import { useSelector } from '~/store';
import { selectBusinessUuid } from '~/store/slices/formSlice/selectors';
import { ApiResponseEnum, apiResponseHandler } from '~/utils/api';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import * as Styles from './styles';
import TextButton from '~/components/TextButton';

interface ISendInvoiceModalProps {
  isModalOpen: boolean;
  onCloseModal(): void;
  invoiceUuid: string;
  onOpenClientModal(): void;
  successCallback?(): void;
}

interface ISendInvoiceData {
  send_from_email: string;
  send_from_name: string;
  recipient_email?: string;
  recipient_name?: string;
  subject: string;
  content: string;
  invoice_uuid: string;
  pdf_url: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  total_amount: string;
  currency: string;
  business_name: string;
  is_attachment_enabled: boolean;
}

const SendInvoiceModal = ({
  isModalOpen,
  onCloseModal,
  invoiceUuid,
  onOpenClientModal,
  successCallback,
}: ISendInvoiceModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendData, setSendData] = useState<ISendInvoiceData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [isAttachmentEnabled, setIsAttachmentEnabled] = useState(true);
  const businessUuid = useSelector(selectBusinessUuid);
  const isDesktop = useIsDesktop();

  const fetchSendData = async () => {
    setIsLoading(true);
    setError(null);

    const promise = checkInvoiceSendApi(businessUuid, invoiceUuid);
    const response = await apiResponseHandler<ISendInvoiceData>(promise);

    if (response.type === ApiResponseEnum.Success) {
      setSendData(response.data);
      setSubject(response.data.subject);
      setContent(response.data.content);
      setIsAttachmentEnabled(response.data.is_attachment_enabled);
    } else {
      setError(response.error);
    }
    setIsLoading(false);
  };

  const handleSendInvoice = async () => {
    setIsSending(true);

    const requestData = {
      subject,
      content,
      is_attachment_enabled: isAttachmentEnabled,
    };

    const promise = sendInvoiceApi(businessUuid, invoiceUuid, requestData);
    const response = await apiResponseHandler(promise);

    if (response.type === ApiResponseEnum.Success) {
      toast.success('Invoice sent successfully!');
      if (successCallback) {
        successCallback();
      }
      onCloseModal();
    } else {
      toast.error('Failed to email invoice. Please try again.');
    }
    setIsSending(false);
  };

  const handleOpenClientEdit = () => {
    onCloseModal();
    onOpenClientModal();
  };

  useEffect(() => {
    if (isModalOpen && invoiceUuid) {
      fetchSendData();
    }
  }, [isModalOpen, invoiceUuid]);

  return (
    <ModalWrapper
      isVisible={isModalOpen}
      modalWidth={isDesktop ? '900px' : 'calc(100vw - 24px)'}
      handleClose={onCloseModal}
    >
      <Styles.Wrapper className="full-width">
        <Styles.StickyHeader>
          <ModalHeader handleClose={onCloseModal} headerJsx="Email Invoice" />
        </Styles.StickyHeader>

        <Styles.ScrollableContent>
          {isLoading && (
            <Styles.LoadingContainer>
              <Text size="xs">Loading...</Text>
            </Styles.LoadingContainer>
          )}

          {error && (
            <Styles.ErrorContainer>
              <Text size="sm" variant="danger">
                {error}
              </Text>
            </Styles.ErrorContainer>
          )}

          {!isLoading && !error && sendData && !sendData.recipient_email && (
            <Styles.NoEmailContainer>
              <Text size="xs" className="text-center">
                Please add an email address to the client details before sending the invoice.
              </Text>
              <Gap t={16} />
              <Button variant="primary" onClick={handleOpenClientEdit} className="full-width">
                Edit Client Details
              </Button>
            </Styles.NoEmailContainer>
          )}

          {!isLoading && !error && sendData && sendData.recipient_email && (
            <Styles.MainContent>
              <Styles.LeftPanel>
                {/* To Section */}
                <Styles.Section>
                  <Styles.SectionLabel>
                    <Text size="xs" className="semi-bold">
                      To
                    </Text>
                  </Styles.SectionLabel>
                  <Styles.RecipientCard>
                    <div>
                      <Text size="xs" className="semi-bold">
                        {sendData.recipient_name}
                      </Text>
                      <Text size="xs" variant="neutral">
                        &lt;{sendData.recipient_email}&gt;
                      </Text>
                    </div>
                    {/* <Button variant="secondary" size="sm" onClick={handleOpenClientEdit}>
                      Edit Client
                    </Button> */}
                    <TextButton size="sm" underline onClick={handleOpenClientEdit}>
                      Edit Client
                    </TextButton>
                  </Styles.RecipientCard>
                </Styles.Section>

                <Gap t={20} />

                {/* Subject Section */}
                <Styles.Section>
                  <Styles.SectionLabel>
                    <Text size="xs" className="semi-bold">
                      Subject
                    </Text>
                  </Styles.SectionLabel>
                  <StyledInput
                    type="text"
                    value={subject}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setSubject(e.target.value)
                    }
                    placeholder="Enter email subject"
                  />
                </Styles.Section>

                <Gap t={20} />

                {/* Message Section */}
                <Styles.Section>
                  <Styles.SectionLabel>
                    <Text size="xs" className="semi-bold">
                      Message
                    </Text>
                  </Styles.SectionLabel>
                  <StyledTextArea
                    value={content}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setContent(e.target.value)
                    }
                    placeholder="Enter your message"
                    rows={8}
                    style={{ minHeight: '150px' }}
                  />
                </Styles.Section>

                <Gap t={20} />

                {/* Attachment Checkbox */}
                <Styles.CheckboxContainer>
                  <Styles.Checkbox
                    type="checkbox"
                    id="attach-pdf"
                    checked={isAttachmentEnabled}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setIsAttachmentEnabled(e.target.checked)
                    }
                  />
                  <label htmlFor="attach-pdf">
                    <Text size="xs">Attach PDF to email</Text>
                  </label>
                </Styles.CheckboxContainer>
              </Styles.LeftPanel>

              <Styles.RightPanel>
                <Text
                  size="xs"
                  className="semi-bold"
                  style={{ color: '#6c757d', letterSpacing: '0.5px' }}
                >
                  EMAIL PREVIEW
                </Text>
                <Gap t={18} />

                <EmailPreview
                  fromEmail={sendData.send_from_email}
                  recipientName={sendData.recipient_name || 'Customer'}
                  senderName={sendData.send_from_name}
                  businessName={sendData.business_name}
                  content={content}
                  invoiceNumber={sendData.invoice_number}
                  invoiceDate={sendData.invoice_date}
                  dueDate={sendData.due_date}
                  totalAmount={sendData.total_amount}
                  currency={sendData.currency}
                  invoiceUrl={`${process.env.LANDING_PAGE_URL}/shared/${sendData.invoice_uuid}/`}
                />
              </Styles.RightPanel>
            </Styles.MainContent>
          )}
        </Styles.ScrollableContent>

        {!isLoading && !error && sendData && sendData.recipient_email && (
          <Styles.StickyFooter>
            <Styles.ButtonContainer>
              <Button variant="secondary" onClick={onCloseModal} size="md">
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSendInvoice}
                disabled={isSending || !subject.trim() || !content.trim()}
                size="md"
              >
                {isSending ? 'Sending...' : 'Email Invoice'}
              </Button>
            </Styles.ButtonContainer>
          </Styles.StickyFooter>
        )}
      </Styles.Wrapper>
    </ModalWrapper>
  );
};

export default SendInvoiceModal;
